import './Main.css';
import EnvironmentBadge from '../EnvironmentBadge/EnvironmentBadge';
import { useEffect, useRef } from 'react';
import { handleStatus, usePollStatus } from '../../core/services/status';
import { useAppContext } from '../../core/context';
import { useNavigation } from '../../platform/navigation/useNavigation';
import Header from '../Header/Header';
import setBrandSpecificHeadMetaData from '../../core/util/setBrandSpecificHeadMetaData';
import { getBrandFromQueryParamOrClaimsOrHost } from '../../core/context/claims';
import CancelSection from '../CancelSection/CancelSection';
import Footer from '../Footer/Footer';
import { initIframeResizeReporter } from '../../core/util/iframeResize';
import { useIframe } from '../../platform/iframe/useIframe';
import { useDevice } from '../../platform/device/useDevice';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import { useFlag } from '../../feature-flags';

interface MainProps {
  children?: React.ReactNode;
  showCancelLink: boolean;
}

function Main({ children, showCancelLink }: MainProps): React.ReactNode {
  const context = useAppContext();
  const showNewDesign = useFlag('redesign');
  const languageContext = useLanguageContext();
  const status = usePollStatus(context);
  const navigation = useNavigation();
  const iframe = useIframe();
  const device = useDevice();
  const parentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const isFirstRouteRef = useRef(true);

  useEffect(() => {
    handleStatus(context, status, navigation, iframe, device);
  }, [context, status, navigation, iframe, device]);

  useEffect(() => {
    const brand = getBrandFromQueryParamOrClaimsOrHost(context?.claims);
    setBrandSpecificHeadMetaData(brand);
    document.body.className = brand;
    document.body?.classList?.toggle('redesign', showNewDesign);
  }, [context, showNewDesign]);

  useEffect(() => {
    if (!parentRef.current) return;
    return initIframeResizeReporter(parentRef.current, iframe);
  }, [iframe]);

  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [context.currentRoute]);

  return (
    <div className={`parent${iframe.isWidgetSdkEmbed() ? ' widget' : ''}`} ref={parentRef}>
      <a className="skip-link" href="#main-content">
        {languageContext.getLocalizedText('skipToContent')}
      </a>
      <Header />
      <div className="main">
        <main id="main-content" className="card" tabIndex={-1} ref={mainRef}>
          <EnvironmentBadge />
          {children}
        </main>
        <CancelSection showCancelLink={showCancelLink} />
      </div>
      <Footer />
    </div>
  );
}

export default Main;
